import React, { useEffect, useMemo, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { api, getApiErrorMessage } from "@/lib/api";
import {
  CalendarDays,
  Edit2,
  Loader2,
  Plus,
  Send,
  Trash2,
} from "lucide-react";

const MAX_SCHEDULES = 3;
const MONTH_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "long",
  year: "numeric",
});
const TABLE_MONTH_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  year: "numeric",
});

const emptyDays = () => "0".repeat(31);

const createSchedule = () => ({
  id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  status: 1,
  start: "00:00",
  stop: "00:00",
  days: emptyDays(),
});

const toDayList = (days = emptyDays()) =>
  days
    .split("")
    .map((value, index) => (value === "1" ? index + 1 : null))
    .filter(Boolean);

const toDaysString = (selectedDays) =>
  Array.from({ length: 31 }, (_, index) =>
    selectedDays.includes(index + 1) ? "1" : "0",
  ).join("");

const formatTime = (value) => {
  if (!value) return "12:00 AM";
  const [hourRaw, minute = "00"] = value.split(":");
  const hour = Number(hourRaw);
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minute.padStart(2, "0")} ${period}`;
};

const buildDefaultAutomation = (device) => ({
  device_id: device?.device_id || "",
  device_type: "v_controller",
  tanks: (device?.tanks || []).map((tank) => ({
    tank_id: tank.tank_id,
    status: 1,
    tlow: Number.isFinite(Number(tank.low)) ? Number(tank.low) : 40,
    thigh: Number.isFinite(Number(tank.high)) ? Number(tank.high) : 90,
  })),
  valves: (device?.valves || []).map((valve) => ({
    valve_id: valve.valve_id,
    status: 1,
    schedules: [],
  })),
});

const buildDefaultPumpAutomation = (device) => ({
  device_id: device?.device_id || "",
  device_type: "p_controller",
  pumps: (device?.pumps || []).map((pump) => ({
    pump_id: pump.pump_id,
    vc_status: true,
    ugt_status: true,
    sch_status: true,
    ugt_low: 0,
    ugt_high: 0,
    schedules: [],
  })),
});

const buildDefaultBorewellAutomation = (device) => ({
  device_id: device?.device_id || "",
  device_type: "b_controller",
  pumps: (device?.pumps || []).map((pump) => ({
    pump_id: pump.pump_id,
    source_status: true,
    source_min_level_m: 0,
    source_tank_low: 0,
    source_tank_high: 0,
    tank_status: true,
    tank1_low: 0,
    tank1_high: 0,
    tank2_low: 0,
    tank2_high: 0,
    utilization_status: true,
    utilization_limit_m3: 0,
    sch_status: true,
    schedules: [],
  })),
});

const mergeAutomation = (device, automation) => {
  const fallback = buildDefaultAutomation(device);
  if (!automation) return fallback;

  const tankLookup = new Map(
    (automation.tanks || []).map((tank) => [tank.tank_id, tank]),
  );
  const valveLookup = new Map(
    (automation.valves || []).map((valve) => [valve.valve_id, valve]),
  );

  return {
    ...fallback,
    tanks: fallback.tanks.map((tank) => ({ ...tank, ...tankLookup.get(tank.tank_id) })),
    valves: fallback.valves.map((valve) => ({
      ...valve,
      ...valveLookup.get(valve.valve_id),
      schedules: (valveLookup.get(valve.valve_id)?.schedules || []).slice(
        0,
        MAX_SCHEDULES,
      ),
    })),
  };
};

const mergePumpAutomation = (device, automation) => {
  const fallback = buildDefaultPumpAutomation(device);
  if (!automation) return fallback;

  const pumpLookup = new Map(
    (automation.pumps || []).map((pump) => [pump.pump_id, pump]),
  );

  return {
    ...fallback,
    pumps: fallback.pumps.map((pump) => {
      const savedPump = pumpLookup.get(pump.pump_id);
      return {
        ...pump,
        ...savedPump,
        schedules: (savedPump?.schedules || []).slice(0, MAX_SCHEDULES),
      };
    }),
  };
};

const mergeBorewellAutomation = (device, automation) => {
  const fallback = buildDefaultBorewellAutomation(device);
  if (!automation) return fallback;

  const pumpLookup = new Map(
    (automation.pumps || []).map((pump) => [pump.pump_id, pump]),
  );

  return {
    ...fallback,
    pumps: fallback.pumps.map((pump) => {
      const savedPump = pumpLookup.get(pump.pump_id);
      return {
        ...pump,
        ...savedPump,
        schedules: (savedPump?.schedules || []).slice(0, MAX_SCHEDULES),
      };
    }),
  };
};

const getDaysInCurrentMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
};

const getCalendarCells = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const daysInMonth = getDaysInCurrentMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const cells = Array.from({ length: firstDay }, () => null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(day);
  return cells;
};

const ScheduleDialog = ({ open, mode, valveLabel, schedule, onClose, onSave }) => {
  const [draft, setDraft] = useState(createSchedule());

  useEffect(() => {
    if (open) setDraft(schedule || createSchedule());
  }, [open, schedule]);

  const selectedDays = useMemo(() => toDayList(draft.days), [draft.days]);
  const calendarCells = useMemo(() => getCalendarCells(), []);
  const monthLabel = MONTH_FORMATTER.format(new Date());
  // Days before today can't be scheduled. A past day already on an older
  // schedule stays visible and can still be removed, but not added.
  const today = new Date().getDate();
  const isPastDay = (day) => day < today;

  const toggleDay = (day) => {
    const isSelected = selectedDays.includes(day);
    if (!isSelected && isPastDay(day)) return;
    const nextDays = isSelected
      ? selectedDays.filter((item) => item !== day)
      : [...selectedDays, day].sort((a, b) => a - b);
    setDraft((prev) => ({ ...prev, days: toDaysString(nextDays) }));
  };

  const handleSave = () => {
    if (!draft.start || !draft.stop) {
      toast.error("Start and stop time are required");
      return;
    }
    if (!selectedDays.some((day) => !isPastDay(day))) {
      toast.error("Select at least one date from today onwards");
      return;
    }
    // "HH:MM" strings compare correctly as text.
    if (draft.stop <= draft.start) {
      toast.error("Stop time must be later than start time");
      return;
    }
    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, "0")}:${String(
      now.getMinutes(),
    ).padStart(2, "0")}`;
    if (selectedDays.includes(today) && draft.start <= currentTime) {
      toast.error(
        "Start time for today has already passed. Choose a later start time or unselect today.",
      );
      return;
    }
    onSave({ ...draft, status: Number(draft.status ?? 1) });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-[94vh] max-w-2xl overflow-y-auto p-0">
        <DialogHeader className="border-b px-6 py-5">
          <p className="text-sm font-semibold text-slate-500">{valveLabel}</p>
          <DialogTitle className="text-3xl font-bold text-slate-900">
            {mode === "edit" ? "Edit Schedule" : "Add Schedule"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-7 px-6 pb-6 pt-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-base font-semibold text-slate-900">
                Start Time
              </Label>
              <Input
                type="time"
                value={draft.start}
                onChange={(event) =>
                  setDraft((prev) => ({ ...prev, start: event.target.value }))
                }
                className="h-14 text-lg"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-base font-semibold text-slate-900">
                Stop Time
              </Label>
              <Input
                type="time"
                value={draft.stop}
                onChange={(event) =>
                  setDraft((prev) => ({ ...prev, stop: event.target.value }))
                }
                className="h-14 text-lg"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-base font-semibold text-slate-900">
              Select Days (Current Month)
            </Label>
            <Input
              readOnly
              value={selectedDays.join(", ")}
              className="h-14 text-lg"
            />
          </div>

          <div className="mx-auto max-w-sm">
            <h3 className="mb-3 text-center text-xl font-bold text-slate-900">
              {monthLabel}
            </h3>
            <div className="mb-2 grid grid-cols-7 text-center text-xs font-bold text-slate-500">
              {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((day) => (
                <span key={day}>{day}</span>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-2">
              {calendarCells.map((day, index) => {
                if (!day) return <span key={`blank-${index}`} />;
                const selected = selectedDays.includes(day);
                const past = isPastDay(day);
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    disabled={past && !selected}
                    title={
                      past
                        ? selected
                          ? "Past date — click to remove"
                          : "Past dates can't be scheduled"
                        : undefined
                    }
                    className={`aspect-square rounded-full border text-sm font-semibold transition ${
                      selected
                        ? past
                          ? "border-blue-200 bg-blue-200 text-white line-through"
                          : "border-blue-600 bg-blue-600 text-white"
                        : past
                          ? "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300"
                          : "border-slate-200 bg-white text-slate-900 hover:border-blue-300"
                    }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-center text-xs text-slate-500">
              Past dates can't be selected.
            </p>
          </div>

          <Button className="h-14 w-full text-lg font-bold" onClick={handleSave}>
            Save Schedule
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Automation = () => {
  const mountedRef = useRef(true);
  const pendingAutomationWaitRef = useRef(null);
  const [deviceKind, setDeviceKind] = useState("level");
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [automation, setAutomation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [dialogState, setDialogState] = useState(null);

  const currentMonthShort = TABLE_MONTH_FORMATTER.format(new Date());
  const automationTanks = Array.isArray(automation?.tanks)
    ? automation.tanks
    : [];
  const automationValves = Array.isArray(automation?.valves)
    ? automation.valves
    : [];
  const automationPumps = Array.isArray(automation?.pumps)
    ? automation.pumps
    : [];

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      pendingAutomationWaitRef.current?.cancel?.();
      pendingAutomationWaitRef.current = null;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadDevices = async () => {
      try {
        setLoading(true);
        setSelectedDeviceId("");
        setSelectedDevice(null);
        setAutomation(null);
        const response =
          deviceKind === "level"
            ? await api.getLevelAutomationDevices()
            : deviceKind === "pump"
              ? await api.getPumpAutomationDevices()
              : await api.getBorewellAutomationDevices();
        if (!active) return;
        setDevices(response);
        if (response.length) setSelectedDeviceId(response[0].device_id);
      } catch (error) {
        if (active) {
          toast.error(
            getApiErrorMessage(error, "Failed to load automation devices"),
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    loadDevices();

    return () => {
      active = false;
    };
  }, [deviceKind]);

  useEffect(() => {
    if (!selectedDeviceId) return;
    const selectedOption = devices.find(
      (device) => device.device_id === selectedDeviceId,
    );
    const expectedDeviceType =
      deviceKind === "level"
        ? "v_controller"
        : deviceKind === "pump"
          ? "p_controller"
          : "b_controller";
    if (!selectedOption || selectedOption.device_type !== expectedDeviceType) {
      return;
    }

    let active = true;

    const loadAutomation = async () => {
      try {
        setDeviceLoading(true);
        const response =
          deviceKind === "level"
            ? await api.getLevelAutomation(selectedDeviceId)
            : deviceKind === "pump"
              ? await api.getPumpAutomation(selectedDeviceId)
              : await api.getBorewellAutomation(selectedDeviceId);
        if (!active) return;
        setSelectedDevice(response.device);
        setAutomation(
          deviceKind === "level"
            ? mergeAutomation(response.device, response.automation)
            : deviceKind === "pump"
              ? mergePumpAutomation(response.device, response.automation)
              : mergeBorewellAutomation(response.device, response.automation),
        );
      } catch (error) {
        if (active) {
          toast.error(getApiErrorMessage(error, "Failed to load automation"));
        }
      } finally {
        if (active) setDeviceLoading(false);
      }
    };
    loadAutomation();

    return () => {
      active = false;
    };
  }, [deviceKind, devices, selectedDeviceId]);

  const handleDeviceKindChange = (value) => {
    setSelectedDeviceId("");
    setDevices([]);
    setSelectedDevice(null);
    setAutomation(null);
    setDialogState(null);
    setDeviceKind(value);
  };

  const valveLabel = (valveId) => {
    const valve = selectedDevice?.valves?.find((item) => item.valve_id === valveId);
    return valve?.name || valveId;
  };

  const updateTank = (tankId, field, value) => {
    setAutomation((prev) => ({
      ...prev,
      tanks: (prev?.tanks || []).map((tank) =>
        tank.tank_id === tankId ? { ...tank, [field]: value } : tank,
      ),
    }));
  };

  const updateValve = (valveId, updater) => {
    setAutomation((prev) => ({
      ...prev,
      valves: (prev?.valves || []).map((valve) =>
        valve.valve_id === valveId ? updater(valve) : valve,
      ),
    }));
  };

  const pumpLabel = (pumpId) => {
    const pump = selectedDevice?.pumps?.find((item) => item.pump_id === pumpId);
    return pump?.name || pumpId;
  };

  const updatePump = (pumpId, updater) => {
    setAutomation((prev) => ({
      ...prev,
      pumps: (prev?.pumps || []).map((pump) =>
        pump.pump_id === pumpId ? updater(pump) : pump,
      ),
    }));
  };

  const updateBorewellPump = updatePump;

  const handleScheduleSave = (schedule) => {
    const { valveId, pumpId, scheduleId } = dialogState;
    const updater = (item) => {
      const exists = item.schedules.some((row) => row.id === scheduleId);
      const schedules = exists
        ? item.schedules.map((row) => (row.id === scheduleId ? schedule : row))
        : [...item.schedules, schedule].slice(0, MAX_SCHEDULES);
      return { ...item, schedules };
    };
    if (deviceKind === "level") updateValve(valveId, updater);
    else updatePump(pumpId, updater);
    setDialogState(null);
  };

  const deleteSchedule = (valveId, scheduleId) => {
    updateValve(valveId, (valve) => ({
      ...valve,
      schedules: valve.schedules.filter((schedule) => schedule.id !== scheduleId),
    }));
  };

  const deletePumpSchedule = (pumpId, scheduleId) => {
    updatePump(pumpId, (pump) => ({
      ...pump,
      schedules: pump.schedules.filter((schedule) => schedule.id !== scheduleId),
    }));
  };

  const sendToDevice = async () => {
    if (!automation?.device_id) return;
    setPublishing(true);
    let elapsed = 0;
    const toastId = toast.loading("Waiting for device acknowledgement... 0s / 30s");
    const timer = setInterval(() => {
      elapsed = Math.min(elapsed + 1, 30);
      toast.loading(
        `Waiting for device acknowledgement... ${elapsed}s / 30s`,
        { id: toastId },
      );
    }, 1000);
    let cancelWait;
    const cancelResponse = new Promise((resolve) => {
      cancelWait = () =>
        resolve({
          success: false,
          status: "cancelled",
          message: "Request cancelled",
        });
    });
    const timeoutResponse = new Promise((resolve) => {
      const timeoutId = setTimeout(
        () =>
          resolve({
            success: false,
            status: "timeout",
            message: "Request timed out",
          }),
        31000,
      );
      pendingAutomationWaitRef.current = {
        toastId,
        timer,
        timeoutId,
        cancel: () => {
          clearInterval(timer);
          clearTimeout(timeoutId);
          toast.dismiss(toastId);
          cancelWait?.();
        },
      };
    });

    try {
      const configureRequest =
        deviceKind === "level"
          ? api.configureLevelAutomation(automation.device_id, automation)
          : deviceKind === "pump"
            ? api.configurePumpAutomation(automation.device_id, automation)
            : api.configureBorewellAutomation(automation.device_id, automation);

      const response = await Promise.race([
        configureRequest,
        timeoutResponse,
        cancelResponse,
      ]);

      const pendingWait = pendingAutomationWaitRef.current;
      clearInterval(timer);
      if (pendingWait?.timeoutId) clearTimeout(pendingWait.timeoutId);
      pendingAutomationWaitRef.current = null;

      if (response.status === "cancelled" || !mountedRef.current) {
        toast.dismiss(toastId);
        return;
      }

      if (response.success) {
        const merged =
          deviceKind === "level"
            ? mergeAutomation(selectedDevice, response.automation)
            : deviceKind === "pump"
              ? mergePumpAutomation(selectedDevice, response.automation)
              : mergeBorewellAutomation(selectedDevice, response.automation);
        setAutomation(merged);
        toast.success("Automation configured successfully", { id: toastId });
      } else if (response.status === "fail") {
        toast.error("Automation rejected by device", { id: toastId });
      } else {
        toast.error("Request timed out", { id: toastId });
      }
    } catch (error) {
      const pendingWait = pendingAutomationWaitRef.current;
      clearInterval(timer);
      if (pendingWait?.timeoutId) clearTimeout(pendingWait.timeoutId);
      pendingAutomationWaitRef.current = null;
      if (mountedRef.current) {
        toast.error(getApiErrorMessage(error, "Failed to send automation"), {
          id: toastId,
        });
      } else {
        toast.dismiss(toastId);
      }
    } finally {
      if (mountedRef.current) setPublishing(false);
    }
  };

  return (
    <Layout>
      <div className="pb-10">
        <div className="mb-10 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Automation</h1>
            <p className="mt-1 text-sm text-slate-500">
              Device process automation
            </p>
          </div>
          <div className="grid w-full gap-4 md:w-[42rem] md:grid-cols-[15rem_1fr]">
            <div>
              <Label className="mb-2 block text-sm font-semibold text-slate-700">
                Type
              </Label>
              <Select value={deviceKind} onValueChange={handleDeviceKindChange}>
                <SelectTrigger className="h-11 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="level">Level Controller</SelectItem>
                  <SelectItem value="pump">Pump Controller</SelectItem>
                  <SelectItem value="borewell">Borewell Pump Controller</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block text-sm font-semibold text-slate-700">
                Device
              </Label>
              <Select value={selectedDeviceId} onValueChange={setSelectedDeviceId}>
                <SelectTrigger className="h-11 bg-white">
                  <SelectValue placeholder="Select controller" />
                </SelectTrigger>
                <SelectContent>
                  {devices.map((device) => (
                    <SelectItem key={device.device_id} value={device.device_id}>
                      {device.name || device.device_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {loading || deviceLoading ? (
          <Card className="mt-10">
            <CardContent className="flex min-h-64 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </CardContent>
          </Card>
        ) : !automation ? (
          <Card className="mt-10">
            <CardContent className="py-12 text-center text-slate-500">
              No mapped controller found.
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="mt-10">
              <CardHeader className="px-8 pb-5 pt-7">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <CalendarDays className="h-5 w-5 text-blue-600" />
                  {selectedDevice?.name || selectedDeviceId}
                </CardTitle>
              </CardHeader>
              {deviceKind === "level" && (
                <CardContent className="px-8 pb-8">
                  <div className="grid gap-8 md:grid-cols-2">
                    {automationTanks.map((tank, index) => (
                      <div
                        key={tank.tank_id}
                        className="rounded-lg border border-slate-200 bg-slate-50 p-6"
                      >
                        <div className="mb-6 flex items-center justify-between gap-4">
                          <div>
                            <p className="font-semibold text-slate-900">
                              Tank {index + 1}
                            </p>
                            <p className="text-sm text-slate-500">Tank level threshold</p>
                          </div>
                          <Switch
                            checked={tank.status === 1}
                            onCheckedChange={(checked) =>
                              updateTank(tank.tank_id, "status", checked ? 1 : 0)
                            }
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-5">
                          <div className="space-y-3">
                            <Label>Low</Label>
                            <Input
                              className="h-11"
                              type="number"
                              min="0"
                              max="100"
                              value={tank.tlow}
                              onChange={(event) =>
                                updateTank(
                                  tank.tank_id,
                                  "tlow",
                                  Number(event.target.value),
                                )
                              }
                            />
                          </div>
                          <div className="space-y-3">
                            <Label>High</Label>
                            <Input
                              className="h-11"
                              type="number"
                              min="0"
                              max="100"
                              value={tank.thigh}
                              onChange={(event) =>
                                updateTank(
                                  tank.tank_id,
                                  "thigh",
                                  Number(event.target.value),
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>

            {deviceKind === "level" && automationValves.map((valve) => (
              <Card key={valve.valve_id} className="mt-10">
                <CardHeader className="px-8 py-7">
                  <CardTitle className="text-lg">
                    {valveLabel(valve.valve_id)}
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-8 pb-8">
                  <div className="space-y-4 rounded-lg border border-slate-200 px-5 py-4">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <Label>Schedule</Label>
                        <Switch
                          checked={valve.status === 1}
                          onCheckedChange={(checked) =>
                            updateValve(valve.valve_id, (item) => ({
                              ...item,
                              status: checked ? 1 : 0,
                            }))
                          }
                        />
                      </div>
                      <Button
                        className="gap-2"
                        disabled={valve.schedules.length >= MAX_SCHEDULES}
                        onClick={() =>
                          setDialogState({
                            mode: "add",
                            valveId: valve.valve_id,
                            schedule: createSchedule(),
                          })
                        }
                      >
                        <Plus className="h-4 w-4" />
                        Add Schedule
                      </Button>
                    </div>
                    <div className="overflow-hidden rounded-lg border border-slate-200">
                      <Table>
                        <TableHeader className="bg-slate-50">
                          <TableRow>
                            <TableHead className="w-16">SR</TableHead>
                            <TableHead>Enable</TableHead>
                            <TableHead>Start Time</TableHead>
                            <TableHead>Stop Time</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-right">Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {valve.schedules.length ? (
                            valve.schedules.map((schedule, index) => (
                              <TableRow key={schedule.id} className="h-14">
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>
                                  <Switch
                                    checked={schedule.status === 1}
                                    onCheckedChange={(checked) =>
                                      updateValve(valve.valve_id, (item) => ({
                                        ...item,
                                        schedules: item.schedules.map((row) =>
                                          row.id === schedule.id
                                            ? { ...row, status: checked ? 1 : 0 }
                                            : row,
                                        ),
                                      }))
                                    }
                                  />
                                </TableCell>
                                <TableCell>{formatTime(schedule.start)}</TableCell>
                                <TableCell>{formatTime(schedule.stop)}</TableCell>
                                <TableCell>
                                  {toDayList(schedule.days).length
                                    ? `${currentMonthShort}(${toDayList(schedule.days).join(", ")})`
                                    : "-"}
                                </TableCell>
                                <TableCell>
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0"
                                      onClick={() =>
                                        setDialogState({
                                          mode: "edit",
                                          valveId: valve.valve_id,
                                          scheduleId: schedule.id,
                                          schedule,
                                        })
                                      }
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                                      onClick={() =>
                                        deleteSchedule(valve.valve_id, schedule.id)
                                      }
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell
                                colSpan={6}
                                className="py-8 text-center text-slate-500"
                              >
                                No schedules added.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {deviceKind === "pump" &&
              automationPumps.map((pump) => (
                <Card key={pump.pump_id} className="mt-10">
                  <CardHeader className="px-8 py-7">
                    <CardTitle className="text-lg">
                      {pumpLabel(pump.pump_id)} ({selectedDeviceId})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6 px-8 pb-8">
                    <div className="grid gap-6 md:grid-cols-2">
                      <div className="h-full rounded-lg border border-slate-200 px-5 py-4">
                        <div className="flex items-center justify-between gap-4">
                          <Label>Valve controller status</Label>
                          <Switch
                            checked={Boolean(pump.vc_status)}
                            onCheckedChange={(checked) =>
                              updatePump(pump.pump_id, (item) => ({
                                ...item,
                                vc_status: checked,
                              }))
                            }
                          />
                        </div>
                      </div>

                      <div className="space-y-4 rounded-lg border border-slate-200 px-5 py-4">
                        <div className="flex items-center justify-between gap-4">
                          <Label>UGT level threshold</Label>
                          <Switch
                            checked={Boolean(pump.ugt_status)}
                            onCheckedChange={(checked) =>
                              updatePump(pump.pump_id, (item) => ({
                                ...item,
                                ugt_status: checked,
                                ...(checked
                                  ? {}
                                  : { ugt_low: 0, ugt_high: 100 }),
                              }))
                            }
                          />
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label className="text-slate-600">UGT Low</Label>
                            <Input
                              className="h-11"
                              type="number"
                              min="0"
                              max="100"
                              value={pump.ugt_low}
                              onChange={(event) =>
                                updatePump(pump.pump_id, (item) => ({
                                  ...item,
                                  ugt_low: Number(event.target.value),
                                }))
                              }
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-slate-600">UGT High</Label>
                            <Input
                              className="h-11"
                              type="number"
                              min="0"
                              max="100"
                              value={pump.ugt_high}
                              onChange={(event) =>
                                updatePump(pump.pump_id, (item) => ({
                                  ...item,
                                  ugt_high: Number(event.target.value),
                                }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4 rounded-lg border border-slate-200 px-5 py-4">
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <Label>Schedule</Label>
                          <Switch
                            checked={Boolean(pump.sch_status)}
                            onCheckedChange={(checked) =>
                              updatePump(pump.pump_id, (item) => ({
                                ...item,
                                sch_status: checked,
                              }))
                            }
                          />
                        </div>
                        <Button
                          className="gap-2"
                          disabled={pump.schedules.length >= MAX_SCHEDULES}
                          onClick={() =>
                            setDialogState({
                              mode: "add",
                              pumpId: pump.pump_id,
                              schedule: createSchedule(),
                            })
                          }
                        >
                          <Plus className="h-4 w-4" />
                          Add Schedule
                        </Button>
                      </div>

                      <div className="overflow-hidden rounded-lg border border-slate-200">
                        <Table>
                          <TableHeader className="bg-slate-50">
                            <TableRow>
                              <TableHead className="w-16">SR</TableHead>
                              <TableHead>Enable</TableHead>
                              <TableHead>Start Time</TableHead>
                              <TableHead>Stop Time</TableHead>
                              <TableHead>Date</TableHead>
                              <TableHead className="text-right">Action</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {pump.schedules.length ? (
                              pump.schedules.map((schedule, index) => (
                                <TableRow key={schedule.id} className="h-14">
                                  <TableCell>{index + 1}</TableCell>
                                  <TableCell>
                                    <Switch
                                      checked={schedule.status === 1}
                                      onCheckedChange={(checked) =>
                                        updatePump(pump.pump_id, (item) => ({
                                          ...item,
                                          schedules: item.schedules.map((row) =>
                                            row.id === schedule.id
                                              ? { ...row, status: checked ? 1 : 0 }
                                              : row,
                                          ),
                                        }))
                                      }
                                    />
                                  </TableCell>
                                  <TableCell>{formatTime(schedule.start)}</TableCell>
                                  <TableCell>{formatTime(schedule.stop)}</TableCell>
                                  <TableCell>
                                    {toDayList(schedule.days).length
                                      ? `${currentMonthShort}(${toDayList(schedule.days).join(", ")})`
                                      : "-"}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex justify-end gap-2">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={() =>
                                          setDialogState({
                                            mode: "edit",
                                            pumpId: pump.pump_id,
                                            scheduleId: schedule.id,
                                            schedule,
                                          })
                                        }
                                      >
                                        <Edit2 className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                                        onClick={() =>
                                          deletePumpSchedule(pump.pump_id, schedule.id)
                                        }
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell
                                  colSpan={6}
                                  className="py-8 text-center text-slate-500"
                                >
                                  No schedules added.
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

            {deviceKind === "borewell" &&
              automationPumps.map((pump) => (
                <Card key={pump.pump_id} className="mt-10">
                  <CardHeader className="px-8 py-7">
                    <CardTitle className="text-lg">{pumpLabel(pump.pump_id)}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-7 px-8 pb-8">
                    <div className="grid gap-8 lg:grid-cols-3">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-6">
                        <div className="mb-6 flex items-center justify-between gap-4">
                          <Label className="font-semibold">Source</Label>
                          <Switch
                            checked={Boolean(pump.source_status)}
                            onCheckedChange={(checked) =>
                              updateBorewellPump(pump.pump_id, (item) => ({
                                ...item,
                                source_status: checked,
                                ...(checked
                                  ? {}
                                  : {
                                      source_min_level_m: 0,
                                      source_tank_low: 0,
                                      source_tank_high: 100,
                                    }),
                              }))
                            }
                          />
                        </div>
                        <div className="grid gap-5">
                          <div className="space-y-3">
                            <Label>Min Level (m)</Label>
                            <Input
                              className="h-11"
                              type="number"
                              min="0"
                              value={pump.source_min_level_m}
                              onChange={(event) =>
                                updateBorewellPump(pump.pump_id, (item) => ({
                                  ...item,
                                  source_min_level_m: Number(event.target.value),
                                }))
                              }
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-5">
                            <div className="space-y-3">
                              <Label>Low</Label>
                              <Input
                                className="h-11"
                                type="number"
                                min="0"
                                max="100"
                                value={pump.source_tank_low}
                                onChange={(event) =>
                                  updateBorewellPump(pump.pump_id, (item) => ({
                                    ...item,
                                    source_tank_low: Number(event.target.value),
                                  }))
                                }
                              />
                            </div>
                            <div className="space-y-3">
                              <Label>High</Label>
                              <Input
                                className="h-11"
                                type="number"
                                min="0"
                                max="100"
                                value={pump.source_tank_high}
                                onChange={(event) =>
                                  updateBorewellPump(pump.pump_id, (item) => ({
                                    ...item,
                                    source_tank_high: Number(event.target.value),
                                  }))
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-6">
                        <div className="mb-6 flex items-center justify-between gap-4">
                          <Label className="font-semibold">Tank</Label>
                          <Switch
                            checked={Boolean(pump.tank_status)}
                            onCheckedChange={(checked) =>
                              updateBorewellPump(pump.pump_id, (item) => ({
                                ...item,
                                tank_status: checked,
                                ...(checked
                                  ? {}
                                  : {
                                      tank1_low: 0,
                                      tank1_high: 100,
                                      tank2_low: 0,
                                      tank2_high: 100,
                                    }),
                              }))
                            }
                          />
                        </div>
                        <div className="grid gap-5">
                          <div className="grid grid-cols-2 gap-5">
                            <div className="space-y-3">
                              <Label>Tank 1 Low</Label>
                              <Input
                                className="h-11"
                                type="number"
                                min="0"
                                max="100"
                                value={pump.tank1_low}
                                onChange={(event) =>
                                  updateBorewellPump(pump.pump_id, (item) => ({
                                    ...item,
                                    tank1_low: Number(event.target.value),
                                  }))
                                }
                              />
                            </div>
                            <div className="space-y-3">
                              <Label>Tank 1 High</Label>
                              <Input
                                className="h-11"
                                type="number"
                                min="0"
                                max="100"
                                value={pump.tank1_high}
                                onChange={(event) =>
                                  updateBorewellPump(pump.pump_id, (item) => ({
                                    ...item,
                                    tank1_high: Number(event.target.value),
                                  }))
                                }
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-5">
                            <div className="space-y-3">
                              <Label>Tank 2 Low</Label>
                              <Input
                                className="h-11"
                                type="number"
                                min="0"
                                max="100"
                                value={pump.tank2_low}
                                onChange={(event) =>
                                  updateBorewellPump(pump.pump_id, (item) => ({
                                    ...item,
                                    tank2_low: Number(event.target.value),
                                  }))
                                }
                              />
                            </div>
                            <div className="space-y-3">
                              <Label>Tank 2 High</Label>
                              <Input
                                className="h-11"
                                type="number"
                                min="0"
                                max="100"
                                value={pump.tank2_high}
                                onChange={(event) =>
                                  updateBorewellPump(pump.pump_id, (item) => ({
                                    ...item,
                                    tank2_high: Number(event.target.value),
                                  }))
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-6">
                        <div className="mb-6 flex items-center justify-between gap-4">
                          <Label className="font-semibold">Utilization</Label>
                          <Switch
                            checked={Boolean(pump.utilization_status)}
                            onCheckedChange={(checked) =>
                              updateBorewellPump(pump.pump_id, (item) => ({
                                ...item,
                                utilization_status: checked,
                                ...(checked ? {} : { utilization_limit_m3: 0 }),
                              }))
                            }
                          />
                        </div>
                        <div className="space-y-3">
                          <Label>Limit (m3)</Label>
                          <Input
                            className="h-11"
                            type="number"
                            min="0"
                            value={pump.utilization_limit_m3}
                            onChange={(event) =>
                              updateBorewellPump(pump.pump_id, (item) => ({
                                ...item,
                                utilization_limit_m3: Number(event.target.value),
                              }))
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4 rounded-lg border border-slate-200 px-5 py-4">
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <Label>Schedule</Label>
                          <Switch
                            checked={Boolean(pump.sch_status)}
                            onCheckedChange={(checked) =>
                              updateBorewellPump(pump.pump_id, (item) => ({
                                ...item,
                                sch_status: checked,
                              }))
                            }
                          />
                        </div>
                        <Button
                          className="gap-2"
                          disabled={pump.schedules.length >= MAX_SCHEDULES}
                          onClick={() =>
                            setDialogState({
                              mode: "add",
                              pumpId: pump.pump_id,
                              schedule: createSchedule(),
                            })
                          }
                        >
                          <Plus className="h-4 w-4" />
                          Add Schedule
                        </Button>
                      </div>
                      <div className="overflow-hidden rounded-lg border border-slate-200">
                        <Table>
                          <TableHeader className="bg-slate-50">
                            <TableRow>
                              <TableHead className="w-16">SR</TableHead>
                              <TableHead>Enable</TableHead>
                              <TableHead>Start Time</TableHead>
                              <TableHead>Stop Time</TableHead>
                              <TableHead>Date</TableHead>
                              <TableHead className="text-right">Action</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {pump.schedules.length ? (
                              pump.schedules.map((schedule, index) => (
                                <TableRow key={schedule.id} className="h-14">
                                  <TableCell>{index + 1}</TableCell>
                                  <TableCell>
                                    <Switch
                                      checked={schedule.status === 1}
                                      onCheckedChange={(checked) =>
                                        updateBorewellPump(pump.pump_id, (item) => ({
                                          ...item,
                                          schedules: item.schedules.map((row) =>
                                            row.id === schedule.id
                                              ? { ...row, status: checked ? 1 : 0 }
                                              : row,
                                          ),
                                        }))
                                      }
                                    />
                                  </TableCell>
                                  <TableCell>{formatTime(schedule.start)}</TableCell>
                                  <TableCell>{formatTime(schedule.stop)}</TableCell>
                                  <TableCell>
                                    {toDayList(schedule.days).length
                                      ? `${currentMonthShort}(${toDayList(schedule.days).join(", ")})`
                                      : "-"}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex justify-end gap-2">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={() =>
                                          setDialogState({
                                            mode: "edit",
                                            pumpId: pump.pump_id,
                                            scheduleId: schedule.id,
                                            schedule,
                                          })
                                        }
                                      >
                                        <Edit2 className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                                        onClick={() =>
                                          deletePumpSchedule(pump.pump_id, schedule.id)
                                        }
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell
                                  colSpan={6}
                                  className="py-8 text-center text-slate-500"
                                >
                                  No schedules added.
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

            <div className="mt-10 flex flex-wrap gap-3">
              <Button
                className="h-11 gap-2 px-5"
                disabled={publishing}
                onClick={sendToDevice}
              >
                {publishing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Send to Device
              </Button>
            </div>
          </>
        )}
      </div>

      <ScheduleDialog
        open={Boolean(dialogState)}
        mode={dialogState?.mode}
        valveLabel={
          dialogState
            ? deviceKind === "level"
              ? valveLabel(dialogState.valveId)
              : pumpLabel(dialogState.pumpId)
            : ""
        }
        schedule={dialogState?.schedule}
        onClose={() => setDialogState(null)}
        onSave={handleScheduleSave}
      />
    </Layout>
  );
};

export default Automation;
